import { parseChannelMarkupToHtml } from "./channelAndHtml";

export interface MessageLinkPreviewData {
  url: string;
  displayUrl: string;
  host: string;
}

const htmlTagPattern = /(<[^>]+>)/g;
const urlPattern = /(^|[\s([{])((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

export function parseChannelMarkupToDisplayHtml(text: string): string {
  const html = parseChannelMarkupToHtml( text );
  const parts = html.split( htmlTagPattern );
  let insideCode = false;

  return parts.map(( part ) => {
    if( part.startsWith( "<" ) && part.endsWith( ">" )) {
      if( /^<code\b/i.test( part )) insideCode = true;
      if( /^<\/code>/i.test( part )) insideCode = false;
      return part;
    }

    return insideCode ? part : linkifyEscapedText( part );
  }).join( "" );
}

export function extractFirstMessageLink(text: string): MessageLinkPreviewData | null {
  const match = text.match( /(?:https?:\/\/|www\.)[^\s<>"']+/i );
  if( !match ) return null;

  const { value } = splitTrailingPunctuation( match[ 0 ]);
  const url = normalizeMessageURL( value );
  if( !url ) return null;

  try {
    const parsed = new URL( url );
    return {
      url,
      displayUrl: stripURLProtocol( value ),
      host: parsed.hostname.replace( /^www\./i, "" ),
    };
  } catch {
    return null;
  }
}

function linkifyEscapedText(text: string): string {
  return text.replace( urlPattern, ( _match, prefix: string, urlValue: string ) => {
    const { value, trailing } = splitTrailingPunctuation( urlValue );
    const href = normalizeMessageURL( decodeEscapedHTML( value ));
    if( !href ) return `${ prefix }${ urlValue }`;

    return `${ prefix }<a class="message-content-link" href="${ escapeAttribute( href )}" target="_blank" rel="noopener noreferrer">${ value }</a>${ trailing }`;
  });
}

function normalizeMessageURL(value: string): string | null {
  const trimmed = value.trim();
  if( trimmed === "" ) return null;

  const url = /^www\./i.test( trimmed ) ? `https://${ trimmed }` : trimmed;

  try {
    const parsed = new URL( url );
    if( parsed.protocol !== "http:" && parsed.protocol !== "https:" ) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

function splitTrailingPunctuation(value: string): { value: string; trailing: string } {
  let clean = value;
  let trailing = "";

  while( /[.,!?;:]$/.test( clean )) {
    trailing = clean.slice( -1 ) + trailing;
    clean = clean.slice( 0, -1 );
  }

  while( clean.endsWith( ")" ) && countOccurrences( clean, ")" ) > countOccurrences( clean, "(" )) {
    trailing = ")" + trailing;
    clean = clean.slice( 0, -1 );
  }

  return { value: clean, trailing };
}

function countOccurrences(value: string, char: string): number {
  return value.split( char ).length - 1;
}

function stripURLProtocol(value: string): string {
  return value.replace( /^https?:\/\//i, "" );
}

function decodeEscapedHTML(value: string): string {
  return value
    .replace( /&amp;/g, "&" )
    .replace( /&lt;/g, "<" )
    .replace( /&gt;/g, ">" );
}

function escapeAttribute(value: string): string {
  return value
    .replace( /&/g, "&amp;" )
    .replace( /"/g, "&quot;" )
    .replace( /</g, "&lt;" )
    .replace( />/g, "&gt;" );
}
